import React from 'react';
import { Action } from '../types';
import { useEventSystem } from '../../components/EventProvider/EventProvider';
import { useTableCellAction, AcknowledgedValues } from '../../context/TableCellContext';
import styles from './cellActions.module.scss';
import { FormattedMessage } from 'react-intl';
export interface Fields {
  entityId: string;
  fieldClicked: string;
  entity?: any;
}
interface ActionProps {
  action: Action;
  fields: Fields;
}

const UndoButton = React.memo(({ onUndo }: { onUndo: () => void }) => {
  return (
    <button data-testid="cellActionUndoButton" type="button" className="button--link" onClick={onUndo}>
      <span className="button__text">
        <span className={`button__icon ${styles.undoIcon}`}>
          <els-icon icon-name="replay" size="s" />
        </span>
        <FormattedMessage
          id="cellAction.UndoButton.text"
          defaultMessage="Undo"
          description="Undo text for the table cell actions"
        />
      </span>
    </button>
  );
});
UndoButton.displayName = 'UndoButton';

const CellAction = React.memo(({ action, fields }: ActionProps) => {
  const { dispatchEvent } = useEventSystem();
  const { acknowledgedValues, setAcknowledgedValues } = useTableCellAction();

  const initialIconName = action.actionName === 'addGrant' ? 'plus' : 'delete';

  const acknowledgedIds = (acknowledgedValues && acknowledgedValues[action.actionName]) || [];

  const isAcknowledged = acknowledgedIds && acknowledgedIds.length > 0 && acknowledgedIds.includes(fields.entityId);

  function OnButtonClick(): void {
    if (!isAcknowledged) {
      const values = {
        [action.actionName]: [...acknowledgedIds, fields.entityId],
      };

      setAcknowledgedValues((current: AcknowledgedValues) => ({ ...current, ...values }));

      dispatchEvent(action.actionName, fields);
    } else {
      const updatedAcknowledgedIds = acknowledgedIds.filter((id: string) => id !== fields.entityId);
      const values = {
        [action.actionName]: updatedAcknowledgedIds,
      };
      setAcknowledgedValues((current: AcknowledgedValues) => ({ ...current, ...values }));

      dispatchEvent(`${action.actionName}:undo`, fields);
    }
  }

  return (
    <div className={`micro-ui-namespace ${styles.cellAction}`}>
      {!isAcknowledged ? (
        <button data-testid="cellActionButton" type="button" className="button--link" onClick={OnButtonClick}>
          <span className="button__text">
            <span className="button__icon margin-size-4-r">
              <els-icon data-testid="actionButtonIcon" icon-name={initialIconName} />
            </span>
            {action.labels[0]}
          </span>
        </button>
      ) : (
        <>
          <span className={styles.acknowledgedLabel}>
            {action.actionName === 'addGrant' && (
              <els-icon icon-name="check" slot="icon-start" color="var(--positive-green)" />
            )}
            {action.labels[1]}
          </span>
          {action.undoable && <UndoButton onUndo={OnButtonClick} />}
        </>
      )}
    </div>
  );
});

CellAction.displayName = 'CellAction';

export default CellAction;
